import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import { AwardChip, InlineAlert, Loader } from "src/grading/components";
import { useGetStudentDocumentsQuery } from "src/store/reducers/documents/documentsApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import {
  useGetReviewQuery,
  useGetStudentResultQuery,
} from "src/store/reducers/sessions/sessionsApiSlice";
import CategoryResultCard, { formatValue } from "./CategoryResultCard";

const CATEGORIES = ["core", "cardio", "upper"] as const;
const TIER_ART = ["presidential", "national", "participation"] as const;

const navButtonSx = {
  width: 40,
  height: 40,
  border: "1.5px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  color: "text.primary",
  fontSize: 15,
  fontWeight: 800,
} as const;

/** One student's result for a session (design `s.result`): award, category breakdown, next goal, prior window, documents. */
const StudentResultPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { sessionId, studentId } = useParams<{
    sessionId: string;
    studentId: string;
  }>();
  const {
    data: result,
    isLoading,
    isError,
  } = useGetStudentResultQuery({ sessionId, studentId });
  const { data: documents } = useGetStudentDocumentsQuery(studentId);
  const {
    data: review,
    isError: reviewError,
    isFetching: reviewFetching,
  } = useGetReviewQuery(sessionId, { skip: !sessionId });
  const session = review?.session;
  const sessionName = session?.name;

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({
        label: t("tabs.sessions"),
        path: "/grading/sessions",
      }),
    );
    dispatch(
      addBreadcrumb({
        label: sessionName ?? t("console.session"),
        path: `/grading/sessions/${sessionId}`,
      }),
    );
    dispatch(
      addBreadcrumb({ label: t("results.crumb", { defaultValue: "Results" }) }),
    );
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, sessionId, sessionName, t]);

  const order = useMemo(
    () => (review?.students ?? []).map((s) => s.studentId),
    [review],
  );
  const index = order.indexOf(studentId);
  const ready = !reviewFetching && !reviewError;
  const prevId = ready && index > 0 ? order[index - 1] : undefined;
  const nextId =
    ready && index >= 0 && index < order.length - 1
      ? order[index + 1]
      : undefined;
  const goTo = (id: string) =>
    history.push(`/grading/sessions/${sessionId}/results/${id}`);

  const name = result ? `${result.firstName} ${result.lastName}` : "";
  useEffect(() => {
    if (name) document.title = `${name} · ${t("app.name")}`;
  }, [name, t]);

  if (isLoading) return <Loader />;
  if (isError || !result)
    return (
      <InlineAlert severity="error">
        {t("results.notFound", {
          defaultValue: "Result not found or outside your scope.",
        })}
      </InlineAlert>
    );
  const own = (documents ?? []).filter(
    (d) => d.sessionId === sessionId && !d.supersededAt,
  );
  const certificate = own.find((d) => d.type === "certificate");
  const letter = own.find((d) => d.type === "family_letter");

  const goals = CATEGORIES.flatMap((c) => {
    const r = result.categories[c];
    if (!r || r.excluded || r.presidentialMet || r.nextTierGap === null)
      return [];
    const isMile = r.eventId === "pft-mile";
    return [
      `${t(`categories.${c}`)}: ${t(
        isMile ? "results.nextBenchmarkTime" : "results.nextBenchmark",
        {
          gap: isMile ? formatValue("pft-mile", r.nextTierGap) : r.nextTierGap,
          tier: r.nationalMet
            ? t("results.tier.presidential")
            : t("results.tier.national"),
        },
      )}`,
    ];
  });
  const windowLabel = session
    ? `${t(`sessions.window.${session.window}`)} ${session.date.slice(0, 4)}`
    : "";
  const grade = session?.rosterSnapshot.find(
    (r) => r.studentId === studentId,
  )?.gradeLevel;
  const meta = [
    `${
      result.benchmarkSex ??
      t("results.noTable", { defaultValue: "No benchmark table" })
    } · ${t("results.ageLabel", { defaultValue: "age" })} ${result.ageAtTest ?? "—"}`,
    grade ? `${t("results.grade", { defaultValue: "Grade" })} ${grade}` : null,
    sessionName,
    windowLabel || null,
    result.benchmarkSexSource === "school" ? t("results.bothThresholds") : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const tierArt = TIER_ART.find((tier) => tier === result.awardTier);
  const position =
    index >= 0
      ? t("results.position", {
          index: index + 1,
          total: order.length,
          defaultValue: "Student {{index}} of {{total}}",
        })
      : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <IconButton
          aria-label={t("results.previousStudent", {
            defaultValue: "Previous student",
          })}
          disabled={!prevId}
          onClick={() => prevId && goTo(prevId)}
          sx={navButtonSx}
        >
          <span aria-hidden="true">←</span>
        </IconButton>
        <IconButton
          aria-label={t("results.nextStudent", {
            defaultValue: "Next student",
          })}
          disabled={!nextId}
          onClick={() => nextId && goTo(nextId)}
          sx={navButtonSx}
        >
          <span aria-hidden="true">→</span>
        </IconButton>
        {position ? (
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}
          >
            {position}
          </Typography>
        ) : null}
      </Box>

      {reviewError && (
        <InlineAlert severity="warning">
          {t("results.rosterUnavailable", {
            defaultValue:
              "Class order is unavailable right now; previous and next are disabled.",
          })}
        </InlineAlert>
      )}

      <Paper
        sx={{
          borderRadius: `${radius.hero}px`,
          p: { xs: 2.5, md: "26px 30px" },
          display: "grid",
          gap: 3.5,
          alignItems: "center",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "auto minmax(0, 1fr) minmax(0, .9fr)",
          },
        }}
      >
        {tierArt ? (
          <Box
            component="img"
            src={`/assets/tiers/${tierArt}.svg`}
            alt={t("results.awardBadge", {
              award: t(`awards.${tierArt}`),
              defaultValue: "{{award}} badge",
            })}
            sx={{
              width: 104,
              height: 104,
              display: { xs: "none", md: "block" },
            }}
          />
        ) : (
          <Box sx={{ display: { xs: "none", md: "block" } }} />
        )}
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}
        >
          <Box sx={{ alignSelf: "flex-start" }}>
            <AwardChip award={result.awardTier} size="md" />
          </Box>
          <Typography
            variant="h1"
            component="h1"
            sx={{ fontSize: { xs: 30, md: 38 } }}
          >
            {name}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            {meta}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
            {certificate && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                href={certificate.url}
                target="_blank"
                rel="noopener"
              >
                {t("results.certificate")}
              </Button>
            )}
            {letter && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                href={letter.url}
                target="_blank"
                rel="noopener"
                sx={{ borderWidth: 1.5, "&:hover": { borderWidth: 1.5 } }}
              >
                {t("results.letterLanguages", {
                  defaultValue: "Family letter · EN / ES",
                })}
              </Button>
            )}
          </Box>
        </Box>
        <Box
          component="section"
          aria-label={t("results.nextGoal", { defaultValue: "Next goal" })}
          sx={(theme) => ({
            p: "18px 20px",
            borderRadius: `${radius.card}px`,
            bgcolor: theme.palette.tokens.primarySoft,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          })}
        >
          <Typography
            variant="overline"
            component="p"
            sx={{ m: 0, color: "primaryInk", lineHeight: 1 }}
          >
            {t("results.nextGoal", { defaultValue: "Next goal" })}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>
            {goals.length > 0
              ? goals.join(" · ")
              : t("results.allPresidential", {
                  defaultValue:
                    "Every category met Presidential. Keep it up next window.",
                })}
          </Typography>
          <Typography
            sx={{ fontSize: 12.5, lineHeight: 1.4, color: "text.secondary" }}
          >
            {t("results.goalSetting", {
              defaultValue:
                "This is goal-setting data, not a grade. Results go to the family privately.",
            })}
          </Typography>
        </Box>
      </Paper>

      {!result.finalized && (
        <Box
          sx={(theme) => ({
            p: "12px 16px",
            borderRadius: `${radius.control}px`,
            bgcolor: theme.palette.surfaceAlt,
            fontSize: 13,
            fontWeight: 600,
          })}
        >
          {t("results.previewBanner", {
            defaultValue:
              "This is a preview. Awards are calculated when the session is finalized.",
          })}
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {CATEGORIES.map((c) => (
          <CategoryResultCard
            key={c}
            category={c}
            result={result.categories[c]}
            prior={result.priorWindow?.categories[c]}
          />
        ))}
      </Box>
    </Box>
  );
};

export default StudentResultPage;
